import { DIRECT_TARGETS, NOTIFY_TARGETS_MAX, NOTIFY_TEAM_TARGETS, PERMISSION_KEYS, type NotifyTarget } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Field, Icon, Select } from "@sagot/ui";
import type { NotifyRecipient } from "../../types";
import { OptionSelect } from "../action-forms";
import { useRefs } from "../refs";
import { needsMember, needsPermission, needsTeam, retarget } from "./system-model";

const N = "engage.workflows.system.notify";
const F = "engage.workflows.fields";

interface RecipientRowProps {
  recipient: NotifyRecipient;
  position: number;
  disabled: boolean;
  removable: boolean;
  onChange: (recipient: NotifyRecipient) => void;
  onRemove: () => void;
}

function RecipientRow({ recipient, position, disabled, removable, onChange, onRemove }: RecipientRowProps) {
  const { t } = useTranslation();
  const { teams, members } = useRefs();
  const direct = DIRECT_TARGETS.has(recipient.target);
  return (
    <fieldset className="space-y-3 rounded-md border border-line p-3" data-testid="notify-recipient">
      <legend className="px-1 text-xs font-bold text-fg-muted">{t(`${N}.recipient`, { n: position })}</legend>
      <div className="flex items-end gap-2">
        <Field label={t(`${N}.target`)} className="min-w-0 flex-1">
          <Select value={recipient.target} disabled={disabled} onChange={(e) => { onChange(retarget(recipient, e.target.value as NotifyTarget, DIRECT_TARGETS.has(e.target.value as NotifyTarget))); }}>
            {NOTIFY_TEAM_TARGETS.map((target) => <option key={target} value={target}>{t(`${N}.targets.${target}`)}</option>)}
          </Select>
        </Field>
        <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled || !removable} aria-label={t(`${N}.removeRecipient`, { n: position })} onClick={onRemove}>
          <Icon name="trash" />
        </Button>
      </div>
      {needsTeam(recipient.target) ? <OptionSelect label={t(`${F}.team`)} value={recipient.team_id} options={teams} disabled={disabled} empty={t(`${F}.choose`)} onChange={(team_id) => { onChange({ ...recipient, team_id }); }} /> : null}
      {needsMember(recipient.target) ? <OptionSelect label={t(`${F}.member`)} value={recipient.user_id} options={members} disabled={disabled} empty={t(`${F}.choose`)} onChange={(user_id) => { onChange({ ...recipient, user_id }); }} /> : null}
      {needsPermission(recipient.target) ? (
        <Field label={t(`${N}.permission`)} hint={t(`${N}.permissionHint`)}>
          <Select value={recipient.permission ?? ""} disabled={disabled} onChange={(e) => { onChange({ ...recipient, permission: e.target.value || undefined }); }}>
            <option value="">{t(`${F}.choose`)}</option>
            {PERMISSION_KEYS.map((key) => <option key={key} value={key}>{key}</option>)}
          </Select>
        </Field>
      ) : null}
      {direct ? (
        <Field label={t(`${N}.delivery`)} hint={t(`${N}.deliveryHint`)}>
          <Select value={recipient.delivery ?? "preferences"} disabled={disabled} onChange={(e) => { onChange({ ...recipient, delivery: e.target.value === "direct" ? "direct" : undefined }); }}>
            <option value="preferences">{t(`${N}.deliveries.preferences`)}</option>
            <option value="direct">{t(`${N}.deliveries.direct`)}</option>
          </Select>
        </Field>
      ) : null}
    </fieldset>
  );
}

interface NotifyRecipientsProps {
  recipients: readonly NotifyRecipient[];
  disabled: boolean;
  onChange: (recipients: NotifyRecipient[]) => void;
}

/** Who a Notify team members step tells: one or more groups of people (or a shared mailbox), each by a target. */
export function NotifyRecipients({ recipients, disabled, onChange }: NotifyRecipientsProps) {
  const { t } = useTranslation();
  const set = (index: number, next: NotifyRecipient) => { onChange(recipients.map((existing, at) => (at === index ? next : existing))); };
  return (
    <div className="space-y-3">
      <p className="label mb-0">{t(`${N}.who`)}</p>
      {recipients.map((recipient, index) => (
        <RecipientRow
          key={index}
          recipient={recipient}
          position={index + 1}
          disabled={disabled}
          removable={recipients.length > 1}
          onChange={(next) => { set(index, next); }}
          onRemove={() => { onChange(recipients.filter((_, at) => at !== index)); }}
        />
      ))}
      {recipients.length < NOTIFY_TARGETS_MAX ? (
        <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => { onChange([...recipients, { target: "owners_admins" }]); }}>
          <Icon name="plus" />
          {t(`${N}.addRecipient`)}
        </Button>
      ) : (
        <p className="text-xs text-fg-subtle">{t(`${N}.maxRecipients`, { max: NOTIFY_TARGETS_MAX })}</p>
      )}
    </div>
  );
}
